import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { useStartConversation } from '@/hooks/conversations';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';

/**
 * Write to a customer, or to somebody who has only enquired.
 *
 * **The gap the owner found on 2026-08-07**: *"There is no way for an admin to
 * start one. No button, anywhere."* A conversation could be created by sending
 * an invoice, by an email arriving that matched nothing, or by the back-fill
 * command — and by nothing anybody could press.
 *
 * **The recipient is shown and is not editable**, exactly like the reply box.
 * The address comes from their record, so a message cannot be aimed at somebody
 * who is not on one — and this is a control that puts real mail in a real
 * person's inbox.
 *
 * **A subject is required and has no default.** A thread the office opened is
 * about something, and "Message from Honeycomb" on every one of them is a
 * search that returns everything.
 */
export function StartConversationDialog({
  open,
  onOpenChange,
  clientId,
  leadId,
  toName,
  toEmail,
  onStarted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string;
  leadId?: string;
  toName: string;
  /** Null when they have no address — the form says so rather than failing on send. */
  toEmail: string | null;
  onStarted?: (conversationId: string) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {/*
            Keyed on `open`, so reopening starts empty rather than holding the
            last message somebody typed and did not send. A fresh mount is a
            fresh `useState`; a reset effect is four lines that can each be
            forgotten — the same reasoning as `AddLeadDialog`.
          */}
          <StartConversationForm
            key={String(open)}
            {...(clientId === undefined ? {} : { clientId })}
            {...(leadId === undefined ? {} : { leadId })}
            toName={toName}
            toEmail={toEmail}
            {...(onStarted === undefined ? {} : { onStarted })}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function StartConversationForm({
  clientId,
  leadId,
  toName,
  toEmail,
  onStarted,
  onDone,
}: {
  clientId?: string;
  leadId?: string;
  toName: string;
  toEmail: string | null;
  onStarted?: (conversationId: string) => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const start = useStartConversation();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const error = start.error instanceof ApiError ? start.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (toEmail === null) return;

    start.mutate(
      {
        ...(clientId === undefined ? {} : { clientId }),
        ...(leadId === undefined ? {} : { leadId }),
        subject,
        bodyText: body,
      },
      {
        onSuccess: (result) => {
          /*
           * **A refused send is reported as what it is**, the same as the reply
           * box. The thread exists and the message is on it either way, marked
           * with the receiving server's own words — a success toast over a
           * message that never arrived is the specific lie this avoids.
           */
          if (result.status === 'sent') {
            toast.success('Sent.');
          } else if (result.status === 'suppressed') {
            toast.error('Not sent — that address is on the do-not-contact list.');
          } else {
            toast.error(`Not delivered${result.error === undefined ? '' : ` — ${result.error}`}`);
          }

          onStarted?.(result.conversationId);
          onDone();
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Dialog.Title className="text-h3 text-ink-900">Start a conversation</Dialog.Title>
      <Dialog.Description className="text-meta text-ink-500">
        {/* Shown, not editable — the address belongs to their record. */}
        This goes to {toName}
        {toEmail === null ? '' : ` at ${toEmail}`}. Their reply comes back into this thread.
      </Dialog.Description>

      {error ? <FormError>{error.message}</FormError> : null}

      {/*
        **Told before the button, not after it.** `lead.email` and `client.email`
        are both nullable — somebody the business only has a phone number for is
        a real and correct state, and P9 is the rule about not inventing an
        address to satisfy a form. The server refuses this too; this is the
        courtesy.
      */}
      {toEmail === null ? (
        <FormError>
          There is no email address on this record, so there is nowhere to send a message. Add one
          first, or ring them and log the call on an existing conversation.
        </FormError>
      ) : null}

      <Field
        label="Subject"
        name="subject"
        required
        value={subject}
        onChange={(event) => {
          setSubject(event.target.value);
        }}
        hint="What it is about. This is what they see, and what you will search for later."
      />

      <div className="space-y-1.5">
        <Label htmlFor="start-conversation-body">Message</Label>
        <textarea
          id="start-conversation-body"
          name="bodyText"
          rows={8}
          required
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white p-3 text-body"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            start.isPending || toEmail === null || subject.trim() === '' || body.trim() === ''
          }
        >
          {start.isPending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </form>
  );
}
